import { useMemo, useState } from "react";

import { UserContract } from "#/api/contracts.type";
import { useLoadContractRequests } from "#/hooks/useLoadContractRequests";
import { formatTimeStampToHumanReadableShortDateTime } from "../utls/time";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import Typography from "../shared/typography";

const getContractLabel = (contract: UserContract, index: number) => {
  const providerName = contract.signing?.providerSummary?.name;
  if (providerName) return providerName;
  return `Contract ${index + 1}`;
};

const getContractStatus = (contract: UserContract) =>
  contract.signing?.status ?? "unknown";

const getContractTimestamp = (contract: UserContract) =>
  contract.signing?.updated_at || contract.signing?.created_at;

const getSignedUrl = (contract: UserContract) =>
  contract.signedPdfUrl || contract.signedDocument?.signed_url || undefined;

export default function AdminContractsCard() {
  const [hasRequested, setHasRequested] = useState(false);
  const {
    requests,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useLoadContractRequests({ includeAll: true, enabled: hasRequested });

  const contracts = useMemo(() => requests ?? [], [requests]);
  const showResults = hasRequested && !isLoading && !isFetching;

  const handleLoadContracts = () => {
    if (!hasRequested) {
      setHasRequested(true);
    } else {
      refetch();
    }
  };

  return (
    <Card className="mb-12 w-full" variant="cream" elevation={2}>
      <Card.Header variant="cream" className="bg-cream-100">
        <div className="flex flex-col gap-2">
          <Typography variant="h4" className="text-terracotta">
            Contract Inventory
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            Admin-only endpoint that lists every contract visible to Harper.
            Use this sparingly to audit signatures or verify delivery status.
          </Typography>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4 bg-white">
        <div className="flex flex-wrap items-center gap-3">
          <SubmitButton
            size="sm"
            onClick={handleLoadContracts}
            isLoading={isLoading || isFetching}
            hideArrow
          >
            {hasRequested ? "Refresh list" : "Load all contracts"}
          </SubmitButton>
          {showResults && (
            <Typography variant="body2" className="text-brown-600">
              {contracts.length} contract{contracts.length === 1 ? "" : "s"}
              &nbsp;loaded
            </Typography>
          )}
        </div>

        {!hasRequested && (
          <Typography variant="caption" className="text-brown-500">
            Click the button above to call `GET /contracts?all=true`.
          </Typography>
        )}

        {isError && (
          <Typography variant="caption" className="text-danger">
            Unable to load contracts. {error instanceof Error ? error.message : ""}
          </Typography>
        )}

        {showResults && contracts.length === 0 && (
          <Typography variant="body2" className="text-brown-600">
            No contracts were returned for this account.
          </Typography>
        )}

        {showResults && contracts.length > 0 && (
          <ul className="divide-y divide-cream-200 rounded-xl border border-cream-200">
            {contracts.map((contract, index) => {
              const label = getContractLabel(contract, index);
              const status = getContractStatus(contract);
              const timestamp = getContractTimestamp(contract);
              const signedUrl = getSignedUrl(contract);
              return (
                <li key={contract.id ?? contract.signing?.id ?? index} className="p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <Typography variant="subtitle1" className="text-terracotta-700">
                        {label}
                      </Typography>
                      <Typography variant="body2" className="text-brown-600">
                        Status: {status}
                      </Typography>
                      {timestamp && (
                        <Typography variant="caption" className="text-brown-500">
                          Updated {formatTimeStampToHumanReadableShortDateTime(timestamp)}
                        </Typography>
                      )}
                    </div>
                    {signedUrl && (
                      <a
                        href={signedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-terracotta-600 hover:text-terracotta-500"
                      >
                        View PDF
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}
